import React from 'react';
import { images, alohaCancerProject } from '../data/mock';
import { Heart, Users, Target, ExternalLink, Quote } from 'lucide-react';

const AboutSection = () => {
  return (
    <section id="about" className="py-14 sm:py-20 bg-gradient-to-b from-gray-900 to-gray-950 relative overflow-hidden">
      {/* Subtle decorative elements - hidden on mobile */}
      <div className="absolute top-0 left-0 w-64 h-64 bg-pink-500/5 rounded-full blur-3xl hidden sm:block" />
      <div className="absolute bottom-0 right-0 w-64 h-64 bg-orange-500/5 rounded-full blur-3xl hidden sm:block" />

      <div className="max-w-5xl mx-auto px-4 relative z-10">
        {/* Header with Logo */}
        <div className="text-center mb-8 sm:mb-12">
          <div className="inline-flex items-center gap-2 bg-pink-500/10 backdrop-blur-sm px-3 sm:px-4 py-2 rounded-full border border-pink-500/20 mb-4 sm:mb-6">
            <Heart className="w-4 h-4 text-pink-400" />
            <span className="text-pink-400 font-semibold text-xs sm:text-sm">FOR A GREAT CAUSE</span>
            <Heart className="w-4 h-4 text-pink-400" />
          </div>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6 mb-4 sm:mb-6">
            <img 
              src={images.acpLogo} 
              alt="Aloha Cancer Project" 
              className="h-20 sm:h-24 md:h-28 object-contain rounded-xl"
            />
            <div className="text-center sm:text-left">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-black text-white">
                {alohaCancerProject.name}
              </h2>
              <p className="text-transparent bg-clip-text bg-gradient-to-r from-pink-400 to-orange-400 font-semibold text-sm sm:text-base">
                {alohaCancerProject.tagline}
              </p>
              <a 
                href="https://www.alohacancerproject.org" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-pink-400 hover:text-pink-300 text-xs sm:text-sm font-medium mt-1 transition-colors"
              >
                www.alohacancerproject.org
                <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 mb-8 sm:mb-10">
          {/* Left - Welcome & Mission */}
          <div className="space-y-4 sm:space-y-6">
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-4 sm:p-6">
              <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-pink-500 to-orange-500 rounded-xl flex items-center justify-center">
                  <Target className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-white">Our Mission</h3>
              </div>
              <p className="text-white/80 leading-relaxed text-xs sm:text-sm">
                Since <strong className="text-white">2014</strong>, we&apos;ve been a resource and support community for people newly diagnosed with cancer, founded by Honolulu icons <strong className="text-pink-400">Daniel Gray</strong> and <strong className="text-pink-400">Christa Wittmier</strong> (&quot;SuperCW&quot;). We&apos;re here to help redefine life beyond cancer through connection, fellowship, and the power of aloha.
              </p>
            </div>

            <div className="bg-gradient-to-br from-orange-500/10 to-pink-500/10 border border-orange-500/20 rounded-2xl p-4 sm:p-6">
              <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-orange-500 to-yellow-500 rounded-xl flex items-center justify-center">
                  <Heart className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-white">Where Proceeds Go</h3>
              </div>
              <p className="text-white/80 leading-relaxed text-xs sm:text-sm">
                <strong className="text-orange-400">Partial proceeds</strong> from Aloha Tequila & Arts Festival go directly to Aloha Cancer Project 2.0 — continuing the legacy of support, fellowship events, and resources for those battling cancer.
              </p>
            </div>
          </div>

          {/* Right - In Loving Memory */}
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-4 sm:p-6">
            <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-pink-500/50 to-orange-500/50 rounded-xl flex items-center justify-center">
                <span className="text-base sm:text-xl">🕯️</span>
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-white">In Loving Memory</h3>
            </div>
            <p className="text-white/80 leading-relaxed text-xs sm:text-sm mb-3 sm:mb-4">
              In loving memory of <strong className="text-pink-400">Christa Wittmier</strong> and <strong className="text-pink-400">Daniel Gray</strong>, Aloha Cancer Project lives on under the vision of good friends <strong className="text-white">Mark Becker</strong> & <strong className="text-white">Flash Hansen</strong> and the festival organizing team.
            </p>
            <p className="text-white/70 text-xs sm:text-sm leading-relaxed mb-3 sm:mb-4">
              Following the passing of co-founder Daniel Gray in October 2016, the project took a hiatus before being revived in 2017 to continue its mission of supporting those facing cancer with aloha spirit.
            </p>
            
            {/* Fellowship Events */}
            <div className="pt-3 sm:pt-4 border-t border-white/10">
              <div className="flex items-center gap-2 mb-2 sm:mb-3">
                <Users className="w-4 h-4 text-pink-400" />
                <span className="text-xs sm:text-sm font-semibold text-white">Fellowship Events:</span>
              </div>
              <div className="flex flex-wrap gap-1.5 sm:gap-2">
                {['Cooking Demos', 'Yoga Sessions', 'Support Gatherings', 'Aloha Tequila Fest'].map((event, i) => (
                  <span 
                    key={i}
                    className={`px-2 sm:px-3 py-1 rounded-full text-[10px] sm:text-xs font-medium ${
                      i === 3 
                        ? 'bg-gradient-to-r from-pink-500 to-orange-500 text-white' 
                        : 'bg-white/10 text-white/70'
                    }`}
                  >
                    {event}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* A Personal Note from Becker */}
        <div
          data-testid="becker-letter"
          className="mt-8 sm:mt-12 relative bg-gradient-to-br from-pink-500/10 via-orange-500/8 to-yellow-500/5 border border-pink-500/25 rounded-3xl p-6 sm:p-10"
        >
          <div className="absolute -top-4 left-6 sm:left-10 bg-gradient-to-br from-pink-500 to-orange-500 rounded-2xl p-2.5 shadow-lg shadow-pink-500/30">
            <Quote className="w-5 h-5 text-white" />
          </div>

          <div className="pt-2 mb-5">
            <h3 className="text-xl sm:text-2xl font-black text-white">A note from Becker</h3>
            <p className="text-pink-300/80 text-xs sm:text-sm tracking-wider uppercase mt-1">Aloha Cancer Project 2.0</p>
          </div>

          <div className="space-y-4 text-white/85 text-sm sm:text-base leading-relaxed">
            <p>Aloha,</p>
            <p>
              Mahalo for joining us for our very first Aloha Tequila Festival — it truly means more than you know.
            </p>
            <p>
              Aloha Cancer Project was created by two of my best friends, <strong className="text-pink-300">Christa Wittmier</strong> and <strong className="text-pink-300">Daniel Gray</strong>. I hired Christa at Young&apos;s Market many years ago, and we spent five unforgettable years building events that brought so much joy to Oʻahu. Daniel was one of the best friends I&apos;ve ever had. We spent three years together in and out of hospitals, and those years changed me forever.
            </p>
            <p>
              Losing them took a piece of me, but it also grew something in me — a promise. I told them I would carry on the Aloha Cancer Project, and today, standing here with all of you, I&apos;m keeping that promise.
            </p>
            <p>
              I&apos;m incredibly excited to bring this event to life alongside my good friends <strong className="text-white">Maleko McDonnell</strong> of Diner en Blanc and <strong className="text-white">Darrell Chock</strong> of Viper Group Media / Hawaiʻi&apos;s Best Kitchens. We&apos;ve gathered some of the best tequilas in the world, paired with amazing cuisine and inspiring local art for you to enjoy. Hosted by my partner in Aloha Cancer Project <strong className="text-white">Flash Hansen</strong>, we are stoked to invite you to Aloha Tequila Festival.
            </p>
            <p>
              Mahalo for being part of this moment, this mission, and this celebration.
            </p>
          </div>

          <div className="mt-6 pt-5 border-t border-white/10">
            <p className="text-white/70 text-sm sm:text-base">With gratitude,</p>
            <p className="text-transparent bg-clip-text bg-gradient-to-r from-pink-400 via-orange-400 to-yellow-400 text-xl sm:text-2xl font-black mt-1" style={{ fontFamily: "'Brush Script MT', 'Lucida Handwriting', cursive" }}>
              Becker
            </p>
          </div>
        </div>

        {/* Bottom Message */}
        <p className="text-center text-white/60 text-sm">
          When you attend, you&apos;re not just having fun — you&apos;re making a difference ❤️
        </p>
      </div>
    </section>
  );
};

export default AboutSection;
